import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../shared/keys";
import type { AgentConfig, AgentCreate, AgentStatus, AgentSummary } from "../shared/types";

/** Agent list mutations (create, duplicate, pause/resume, publish, delete) + list refresh. */
export function useAgentActions() {
  const api = useApi();
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const { t } = useTranslation();
  const message = useErrorMessage();
  const refresh = () => client.invalidateQueries({ queryKey: buildKeys.agents(ws) });
  const fail = (error: unknown) => {
    toast.error(message(error));
  };
  const path = (agent: Pick<AgentSummary, "id">) => ({ params: { path: { agent_id: agent.id } } });

  const create = useMutation({
    mutationFn: (body: AgentCreate): Promise<AgentConfig> => unwrap(api.POST("/v1/agents", { body })),
    onSuccess: () => {
      toast.success(t("build.agents.createDialog.created"));
      void refresh();
    },
  });
  const duplicate = useMutation({
    mutationFn: (agent: AgentSummary) => unwrap(api.POST("/v1/agents/{agent_id}/duplicate", path(agent))),
    onSuccess: (copy) => {
      toast.success(t("build.agents.toasts.duplicated", { name: copy.name }));
      void refresh();
    },
    onError: fail,
  });
  const setStatus = useMutation({
    mutationFn: ({ agent, status }: { agent: AgentSummary; status: AgentStatus }) =>
      unwrap(api.PATCH("/v1/agents/{agent_id}", { ...path(agent), body: { status } })),
    onSuccess: (updated) => {
      if (updated.status === "paused") toast.warning(t("build.agents.toasts.paused", { name: updated.name }));
      else toast.success(t("build.agents.toasts.resumed", { name: updated.name }));
      void refresh();
    },
    onError: fail,
  });
  const publish = useMutation({
    mutationFn: (agent: AgentSummary) => unwrap(api.POST("/v1/agents/{agent_id}/publish", path(agent))).then(() => agent),
    onSuccess: (agent) => {
      toast.success(t("build.agents.toasts.published", { name: agent.name }));
      void refresh();
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: ({ agent, confirm }: { agent: Pick<AgentSummary, "id">; confirm: string }) =>
      unwrap(api.DELETE("/v1/agents/{agent_id}", { ...path(agent), body: { confirm } })),
    onSuccess: () => {
      toast.success(t("build.agents.deleteDialog.deleted"));
      void refresh();
    },
  });
  return { create, duplicate, setStatus, publish, remove };
}
